import { ButtonLink } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import { useAppForm } from '@repo/ui/hooks'

import { useCheckUser, useEmailOtp, usePhoneOtp } from '@repo/services'
import { useErrorHandler } from '@repo/shared/hooks'
import {
  Button,
  Col,
  Form,
  FormField,
  FormItemWrapper,
  Input,
  Separator,
  Typography,
} from '@repo/ui/components'
import { cn, normalizeDigits, normalizeMobile } from '@repo/ui/lib'
import { useEffect, useState } from 'react'
import { useRegisterSchema } from '../../schemas'
import { IRegisterForm, IRegisterInfo } from './register.types'
import { TermsDialog } from './terms.dialog'

import { Cookie, handleNumericInput, handlePasteInput } from '@repo/tools'
import { toast } from '@repo/ui/components'
import { useSearchParams } from 'next/navigation'

interface RegisterFormProps {
  registerInfo: IRegisterInfo | null
  onSuccess: (
    mobileOrEmail: string,
    invitationCode?: string,
    type?: 'email' | 'mobile',
    otp?: number,
  ) => void
}

export const RegisterForm = ({
  onSuccess,
  registerInfo,
}: RegisterFormProps) => {
  const { t, tRich } = useTranslate()

  const searchParams = useSearchParams()
  const referralCode =
    Cookie.get('referral_code') ?? searchParams.get('refCode')

  const [isEmailRegistration, setIsEmailRegistration] = useState(
    registerInfo?.type === 'email',
  )
  const [isTermsDialogOpen, setIsTermsDialogOpen] = useState(false)
  const phoneOtpMutation = usePhoneOtp()
  const emailOtpMutation = useEmailOtp()
  const { mobileSchema, emailSchema } = useRegisterSchema()
  const { errorHandler } = useErrorHandler()
  const { form } = useAppForm({
    formSchema: isEmailRegistration ? emailSchema : mobileSchema,
    defaultValues: {
      email: registerInfo?.mobileOrEmail,
      mobile: registerInfo?.mobileOrEmail?.replace('+98', '0'),
      invitationCode: referralCode,
    },
  })
  const checkUserMutation = useCheckUser()

  const onSubmit = async (data: IRegisterForm) => {
    const isEmail = isEmailRegistration
    const identifier = isEmail
      ? (data.email ?? '')
      : normalizeMobile(data.mobile)
    const checkUserPayload = isEmail
      ? { email: identifier }
      : { phone: identifier }

    checkUserMutation.mutate(checkUserPayload, {
      onSuccess: (res) => {
        if (res?.exists) {
          toast.error({
            title: t('common_userAlreadyExists'),
          })
          return
        }
        if (isEmail) {
          const invitationCode = form.getValues('invitationCode')
          emailOtpMutation.mutate(
            { email: identifier },
            {
              onSuccess: (res) => {
                onSuccess(identifier, invitationCode, 'email', res?.data?.otp)
              },
              onError: errorHandler,
            },
          )
        } else {
          const invitationCode = form.getValues('invitationCode')
          phoneOtpMutation.mutate(
            { phone: identifier },
            {
              onSuccess: (res) => {
                onSuccess(identifier, invitationCode, 'mobile', res?.data?.otp)
              },
              onError: errorHandler,
            },
          )
        }
      },
      onError: errorHandler,
    })
  }

  const toggleRegistrationType = () => {
    setIsEmailRegistration(!isEmailRegistration)
    form.reset({
      email: '',
      mobile: '',
      invitationCode: form.getValues('invitationCode'),
    })
  }

  useEffect(() => {
    form.setFocus(isEmailRegistration ? 'email' : 'mobile')
  }, [form, isEmailRegistration])

  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='space-y-3xl w-full'
          noValidate
        >
          <FormField
            control={form.control}
            name={isEmailRegistration ? 'email' : 'mobile'}
            render={({ field }) => (
              <FormItemWrapper
                title={t(
                  isEmailRegistration ? 'common_email' : 'common_mobileNumber',
                )}
                caption={
                  isEmailRegistration
                    ? undefined
                    : t('loginPage_mobileStartsWithZero')
                }
                inputSize='lg'
              >
                <Input
                  {...field}
                  name={isEmailRegistration ? 'email' : 'mobile'}
                  type='text'
                  data-error={
                    !!form.formState.errors[
                      isEmailRegistration ? 'email' : 'mobile'
                    ]
                  }
                  onChange={(e) => {
                    if (!isEmailRegistration) {
                      const value = normalizeDigits(e.target.value)
                      field.onChange(value)
                    } else {
                      field.onChange(e)
                    }
                  }}
                  inputSize='lg'
                  variant='fill'
                  className={`ltr ${isEmailRegistration ? 'font-[AzarMehr]' : ''}`}
                  autoComplete='off'
                  onKeyDown={(e) => {
                    if (!isEmailRegistration) {
                      handleNumericInput(e, 11)
                    }
                  }}
                  onPaste={(e) => {
                    if (!isEmailRegistration) {
                      handlePasteInput(e, 11)
                    }
                  }}
                />
              </FormItemWrapper>
            )}
          />

          <FormField
            control={form.control}
            name='invitationCode'
            disabled={referralCode ? true : false}
            render={({ field }) => (
              <FormItemWrapper
                title=''
                caption={
                  referralCode
                    ? t('registerPage_invitationCodeDisabled')
                    : t('registerPage_invitationCodeOptional')
                }
                inputSize='lg'
              >
                <Input
                  placeholder={t('registerPage_invitationCode')}
                  autoComplete='off'
                  inputSize='lg'
                  className={cn(
                    form.watch('invitationCode') && 'ltr font-[AzarMehr]',
                  )}
                  variant={'fill'}
                  {...field}
                />
              </FormItemWrapper>
            )}
          />

          <div className='flex items-center gap-xs'>
            <Typography
              size='sm'
              className='text-center text-neutral-surface-80'
            >
              {tRich.rich('registerPage_acceptTerms', {
                terms: (chunks) => (
                  <ButtonLink
                    href='#'
                    color='base'
                    size='sm'
                    className='underline'
                    type='button'
                    onClick={(e) => {
                      e.preventDefault()
                      setIsTermsDialogOpen(true)
                    }}
                  >
                    {chunks}
                  </ButtonLink>
                ),
              })}
            </Typography>
          </div>

          <Button
            className='w-full'
            type='submit'
            variant='fill'
            size='lg'
            loading={
              phoneOtpMutation.isPending ||
              emailOtpMutation.isPending ||
              checkUserMutation.isPending
            }
          >
            {t('common_register')}
          </Button>

          <Separator title={t('common_or')} />

          <Col className='gap-md'>
            <Button
              className='w-full text-neutral-surface-100'
              variant='ghost'
              size='lg'
              color='neutral'
              onClick={toggleRegistrationType}
              type='button'
            >
              {t(
                isEmailRegistration
                  ? 'registerPage_registerWithMobile'
                  : 'registerPage_registerWithEmail',
              )}
            </Button>
            <ButtonLink
              href='/auth/register/legal'
              variant='ghost'
              size='lg'
              width='full'
              color='neutral'
            >
              {t('registerPage_registerLegalEntity')}
            </ButtonLink>
          </Col>
        </form>
      </Form>

      <TermsDialog
        open={isTermsDialogOpen}
        onOpenChange={setIsTermsDialogOpen}
      />
    </>
  )
}
